/**
 * Reading the backend's structured error body instead of throwing away everything but the status.
 *
 * The API's error shape (`RestExceptionHandler` -> `ApiErrorResponse`) carries `errorCode`,
 * `message` and a `traceId`. Services that threw `new Error(\`...: ${response.status}\`)` discarded
 * all three, so a payment failure reached the user as a bare "502" and reached support as nothing
 * at all — the traceId needed to find the server-side log line never left the browser.
 */

export interface ApiError {
  status: number;
  errorCode?: string;
  message?: string;
  traceId?: string;
}

/** Parses the structured error body; falls back to the status alone when the body isn't ours. */
export async function readApiError(response: Response): Promise<ApiError> {
  try {
    const body = await response.clone().json();
    return {
      status: response.status,
      errorCode: typeof body?.errorCode === "string" ? body.errorCode : undefined,
      message: typeof body?.message === "string" ? body.message : undefined,
      traceId: typeof body?.traceId === "string" ? body.traceId : undefined,
    };
  } catch {
    return { status: response.status };
  }
}

/**
 * User-facing copy. Codes a teacher can act on get a plain-English sentence; anything else falls
 * back to the caller's wording. The traceId is appended when present so a teacher reporting the
 * problem can quote something support can actually grep for.
 */
const FRIENDLY: Record<string, string> = {
  // Reached when a cancelled-but-still-running subscription tries to upgrade. The UI should route
  // these teachers to Renew instead (see PlanActionCell), so this is the backstop for a stale tab
  // or a race, not the normal path -- but without it the teacher saw a bare "409".
  SUBSCRIPTION_CANCELLED:
    "Your subscription is cancelled, so it can't be upgraded \u2014 that would charge you for a tier you'd lose at the end of the current period. Renew it first, then upgrade.",
  PAYMENT_PROVIDER_ERROR:
    "The payment provider rejected this request, so nothing was charged. Please try again, and contact support if it keeps happening.",
  PAYMENT_PROVIDER_UNAVAILABLE:
    "We couldn't reach the payment provider, so nothing was charged. Please try again in a moment.",
  // Teacher-actionable 403s. Without these, every one of them surfaced as the caller's generic
  // "Failed to ...: 403", which tells a teacher nothing they can act on and tells support nothing
  // they can triage -- the server had already named the reason in the body.
  AD_BOOST_NOT_ELIGIBLE:
    "Your subscription already includes priority placement, so an ad boost can't be purchased on top of it.",
  TEACHER_PROFILE_NOT_APPROVED:
    "Your teacher profile needs to be approved before you can make this purchase.",
  BOOST_ADMIN_OVERRIDDEN:
    "Your listing's boost is currently managed by our team, so this purchase isn't available. Please contact support.",
};

export function describeApiError(error: ApiError, fallback: string): string {
  // `message` carries the server's stable code for ResponseStatusException-based failures, so it is
  // checked alongside errorCode rather than shown raw.
  const code = error.errorCode && FRIENDLY[error.errorCode] ? error.errorCode
    : error.message && FRIENDLY[error.message] ? error.message
    : undefined;

  const base = code ? FRIENDLY[code] : fallback;
  return error.traceId ? `${base} (ref: ${error.traceId})` : base;
}

/** Reads the error body and throws an Error whose message is safe to show a user. */
export async function throwApiError(response: Response, fallback: string): Promise<never> {
  throw new Error(describeApiError(await readApiError(response), fallback));
}
